import { useContext } from 'react';
import { AppThemeContext } from '../contexts/AppThemeContext';
import { useTranslation } from '../contexts/TranslationContext';

type BrandLogoVariant = 'platform' | 'mark' | 'public' | 'share';

interface BrandLogoProps {
  variant: BrandLogoVariant;
  className?: string;
  loading?: 'eager' | 'lazy';
}

interface BrandLogoImageSource {
  fallback: string;
  webpSrcSet: string;
  sizes: string;
  width: number;
  height: number;
}

interface BrandLogoSource {
  light: BrandLogoImageSource;
  dark?: BrandLogoImageSource;
}

const LOGO_BY_VARIANT: Record<BrandLogoVariant, BrandLogoSource> = {
  platform: {
    light: {
      fallback: '/assets/logo/palen_platform.png?v=20260807',
      webpSrcSet: [
        '/assets/logo/palen_platform-180.webp?v=20260807 180w',
        '/assets/logo/palen_platform-280.webp?v=20260807 280w',
        '/assets/logo/palen_platform-360.webp?v=20260807 360w',
      ].join(', '),
      sizes: '(min-width: 640px) 224px, 136px',
      width: 774,
      height: 218,
    },
    dark: {
      fallback: '/assets/logo/palen_platform_dark.png?v=20260807',
      webpSrcSet: [
        '/assets/logo/palen_platform_dark-180.webp?v=20260807 180w',
        '/assets/logo/palen_platform_dark-280.webp?v=20260807 280w',
        '/assets/logo/palen_platform_dark-360.webp?v=20260807 360w',
      ].join(', '),
      sizes: '(min-width: 640px) 224px, 136px',
      width: 774,
      height: 218,
    },
  },
  mark: {
    light: {
      fallback: '/assets/logo/palen_p.png',
      webpSrcSet: [
        '/assets/logo/palen_p-48.webp 48w',
        '/assets/logo/palen_p-96.webp 96w',
        '/assets/logo/palen_p-128.webp 128w',
      ].join(', '),
      sizes: '48px',
      width: 209,
      height: 216,
    },
  },
  public: {
    light: {
      fallback: '/assets/logo/palen_public.png',
      webpSrcSet: [
        '/assets/logo/palen_public-224.webp 224w',
        '/assets/logo/palen_public-360.webp 360w',
      ].join(', '),
      sizes: '(min-width: 640px) 288px, 224px',
      width: 774,
      height: 336,
    },
    dark: {
      fallback: '/assets/logo/palen_public_dark.png',
      webpSrcSet: [
        '/assets/logo/palen_public_dark-224.webp 224w',
        '/assets/logo/palen_public_dark-360.webp 360w',
      ].join(', '),
      sizes: '(min-width: 640px) 288px, 224px',
      width: 774,
      height: 336,
    },
  },
  share: {
    light: {
      fallback: '/assets/logo/palen_full_small.png',
      webpSrcSet: '/assets/logo/palen_full_small.webp 1200w',
      sizes: '(min-width: 1280px) 272px, (min-width: 1024px) 224px, (min-width: 640px) 240px, 240px',
      width: 1200,
      height: 379,
    },
    dark: {
      fallback: '/assets/logo/palen_full_small_dark.png',
      webpSrcSet: '/assets/logo/palen_full_small_dark.webp 1200w',
      sizes: '(min-width: 1280px) 272px, (min-width: 1024px) 224px, (min-width: 640px) 240px, 240px',
      width: 1200,
      height: 379,
    },
  },
};

const SIZE_BY_VARIANT: Record<BrandLogoVariant, string> = {
  platform: 'h-8 w-auto sm:h-10',
  mark: 'h-10 w-10 object-contain',
  public: 'h-24 w-auto object-contain sm:h-32',
  share: 'h-24 w-auto object-contain sm:h-24 lg:h-20 xl:h-24',
};

function getBrandLogoClassName(variant: BrandLogoVariant, className?: string) {
  return [SIZE_BY_VARIANT[variant], 'shrink-0 object-contain', className]
    .filter(Boolean)
    .join(' ');
}

export function BrandLogo({
  variant,
  className,
  loading = 'lazy',
}: BrandLogoProps) {
  const { t } = useTranslation();
  const themeContext = useContext(AppThemeContext);
  const logoSources = LOGO_BY_VARIANT[variant];
  const logo = themeContext?.theme === 'dark' && logoSources.dark
    ? logoSources.dark
    : logoSources.light;

  return (
    <picture>
      <source type="image/webp" srcSet={logo.webpSrcSet} sizes={logo.sizes} />
      <img
        src={logo.fallback}
        alt={t('appName')}
        className={getBrandLogoClassName(variant, className)}
        width={logo.width}
        height={logo.height}
        decoding="async"
        loading={loading}
      />
    </picture>
  );
}
